import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Toolbar, Tooltip, IconButton, Typography, Input, InputAdornment, Chip, Stack, CircularProgress, Button } from '@mui/material';
// component
import Iconify from '../../../components/iconify';
import getIMessagesStatistic from '../../../hooks/queries/getMessagesStatistic';
import { fNumber } from '../../../utils/formatNumber';

// ----------------------------------------------------------------------

const StyledRoot = styled(Toolbar)(({ theme }) => ({
  height: 96,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1, 0, 3),
}));

const StyledSearch = styled(Input)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    width: 320,
    boxShadow: theme.customShadows.z8,
  },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${alpha(theme.palette.grey[500], 0.32)} !important`,
  },
}));

// ----------------------------------------------------------------------

MessageListToolbar.propTypes = {
  isMutating: PropTypes.bool,
  isRetrying: PropTypes.bool,
  numSelected: PropTypes.number,
  filterName: PropTypes.string,
  filterStatus: PropTypes.string,
  onFilterName: PropTypes.func,
  onFilterStatus: PropTypes.func,
  onRetryMessages: PropTypes.func,
};

export default function MessageListToolbar({ isMutating, isRetrying, numSelected, filterName, filterStatus, onFilterName, onFilterStatus, onRetryMessages }) {
  const {
    isLoading: isLoadingStatistic,
    data: statistic,
  } = useQuery(['messages', 'statistic'], getIMessagesStatistic, {
    refetchInterval: 10000,
  });

  return (
    <StyledRoot
      sx={{
        ...(numSelected > 0 && {
          color: 'primary.main',
          bgcolor: 'primary.lighter',
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography component="div" variant="subtitle1">
          {numSelected} selected
        </Typography>
      ) : (
        <StyledSearch
          value={filterName}
          onChange={onFilterName}
          placeholder="Search recipient..."
          startAdornment={
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
            </InputAdornment>
          }
        />
      )}

      {!numSelected && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Chip label={`Total: ${isLoadingStatistic ? '...' : fNumber(statistic?.total)}`} variant={filterStatus === 'total' ? '' : 'outlined'} onClick={() => onFilterStatus('total')} />
          <Chip label={`Pending: ${isLoadingStatistic ? '...' : fNumber(statistic?.pending)}`} color="info" variant={filterStatus === 'pending' ? '' : 'outlined'} onClick={() => onFilterStatus('pending')} />
          <Chip label={`Sending: ${isLoadingStatistic ? '...' : fNumber(statistic?.sending)}`} color="info" variant={filterStatus === 'sending' ? '' : 'outlined'} onClick={() => onFilterStatus('sending')} />
          <Chip label={`Sent: ${isLoadingStatistic ? '...' : fNumber(statistic?.sent)}`} color="primary" variant={filterStatus === 'sent' ? '' : 'outlined'} onClick={() => onFilterStatus('sent')} />
          <Chip label={`Delivered: ${isLoadingStatistic ? '...' : fNumber(statistic?.delivered)}`} color="primary" variant={filterStatus === 'delivered' ? '' : 'outlined'} onClick={() => onFilterStatus('delivered')} />
          <Chip label={`Read: ${isLoadingStatistic ? '...' : fNumber(statistic?.read)}`} color="primary" variant={filterStatus === 'read' ? '' : 'outlined'} onClick={() => onFilterStatus('read')} />
          <Chip label={`Success: ${isLoadingStatistic ? '...' : fNumber((statistic?.sent ?? 0) + (statistic?.delivered ?? 0) + (statistic?.read ?? 0))}`} color="success" sx={{ borderColor: 'success.dark', color: 'success.dark' }} variant={filterStatus === 'success' ? '' : 'outlined'} onClick={() => onFilterStatus('success')} />
          <Chip label={`Failed: ${isLoadingStatistic ? '...' : fNumber(statistic?.failed)}`} color="error" variant={filterStatus === 'failed' ? '' : 'outlined'} onClick={() => onFilterStatus('failed')} />
          <Button variant="contained" color='warning' startIcon={isRetrying ? <CircularProgress size="20px" /> : <Iconify icon="eva:flip-2-outline" />} disabled={isMutating} onClick={onRetryMessages}>Retry Sending</Button>
        </Stack>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <Iconify icon="eva:trash-2-fill" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <Iconify icon="ic:round-filter-list" />
          </IconButton>
        </Tooltip>
      )}
    </StyledRoot>
  );
}
